/**
 * ListCard — 나의 현장 · 나의 시공파트너 · 나의 자재파트너 목록 카드 공용 부품.
 *
 * 수치는 Figma 사용 예시(4400:2932, 카드 4366:2817)를 따른다.
 *   - 카드 안쪽 여백 16 · 섹션 간격 16 · 하단 버튼 영역은 카드 바닥 정렬(32px 버튼, 간격 8)
 *   - 헤더 24: 배지(좌) + 편집·삭제 아이콘 버튼(24, 간격 12). 배지가 없으면 제목이 그 자리에 들어간다.
 *   - 라벨/값: 라벨 88 + 간격 8, 줄 간격 8, body-sm
 * 그리드: 1440+ 4열 · 그 아래 3열 · LNB 열림 1280↓ 2열 / 960↓ 1열 · LNB 닫힘 1023↓ 2열 / 720↓ 1열
 *   (styles components.css .card-list.cols-1-2-3-4 + LeftNav 의 <html data-lnb>).
 * 라벨 폭은 88 대신 스케일 값 w-24(96).
 */
import type { ReactNode } from 'react';
import { Card, type CardProps } from '@indipia/ui-core';
import { formatPhone } from '@indipia/shared-features';

/** 카드 그리드 — 뷰포트 × LNB 상태 기준 1·2·3·4열(.card-list.cols-1-2-3-4) */
export function ListCardGrid({ children }: { children: ReactNode }) {
  return <div className="card-list cols-1-2-3-4">{children}</div>;
}

/** accentColor — 카드 상단 색상 띠(현장 대표색 등, 캘린더와 같은 색으로 같은 대상을 잇는다) */
export function ListCard({ className = '', accentColor, style, ...props }: CardProps & { accentColor?: string | null }) {
  return (
    <Card
      className={`flex flex-col [&>.body]:flex [&>.body]:flex-1 [&>.body]:flex-col [&>.body]:gap-4 ${className}`}
      style={accentColor ? { borderTop: `var(--num-4) solid ${accentColor}`, ...style } : style}
      {...props}
    />
  );
}

/** 헤더 — 배지가 없으면(badges 가 비면) title 을 한 줄 말줄임으로 보여준다. onDelete 가 없으면 휴지통을 그리지 않는다 */
export function ListCardHeader({
  badges, title, editLabel, deleteLabel, onEdit, onDelete, deleteDisabled,
}: {
  badges: ReactNode[];
  title: string;
  editLabel: string;
  deleteLabel?: string;
  onEdit: () => void;
  onDelete?: () => void;
  deleteDisabled?: boolean;
}) {
  const shown = badges.filter(Boolean);
  return (
    <div className="flex min-h-6 items-start justify-between gap-2">
      {shown.length > 0 ? (
        <div className="flex min-w-0 flex-wrap items-center gap-2">{shown}</div>
      ) : (
        <span className="min-w-0 truncate pt-1 text-heading-sm font-bold text-text" title={title}>{title}</span>
      )}
      <div className="flex shrink-0 items-center gap-3">
        <button
          type="button"
          title={editLabel}
          aria-label={editLabel}
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          className="flex h-6 w-6 items-center justify-center rounded-sm text-text transition-colors hover:text-point"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
          </svg>
        </button>
        {onDelete && <button
          type="button"
          title={deleteLabel}
          aria-label={deleteLabel}
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          disabled={deleteDisabled}
          className="flex h-6 w-6 items-center justify-center rounded-sm text-text transition-colors hover:text-error disabled:opacity-40"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
            <path d="M10 11v6"/><path d="M14 11v6"/>
          </svg>
        </button>}
      </div>
    </div>
  );
}

/** 이름 · 연락처 · 부가정보 묶음 — hideName 은 헤더가 이미 제목을 보여줄 때. 연락처는 ###-####-#### 로 통일 */
export function ListCardIdentity({
  name, phone, lead, detail, hideName, onNameClick,
}: {
  name: string; phone?: string | null; lead?: string | null; detail?: string | null; hideName?: boolean;
  /** 카드 열기 — 지정하면 이름을 버튼으로 그려 키보드·스크린리더로도 열 수 있게 한다(카드 자체는 role 없이 마우스 클릭만) */
  onNameClick?: () => void;
}) {
  const nameCls = 'block truncate text-heading-sm font-bold text-text';
  return (
    <div className="flex flex-col gap-2">
      {!hideName && (onNameClick ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onNameClick(); }}
          className={`${nameCls} max-w-full text-left hover:text-point hover:underline`}
          title={name}
        >
          {name}
        </button>
      ) : (
        <span className={nameCls} title={name}>{name}</span>
      ))}
      {phone && <span className="block text-body-md text-text">{formatPhone(phone)}</span>}
      {(lead || detail) && (
        <span className="block text-body-md text-text">
          {lead && <b className="font-bold">{lead}</b>}
          {lead && detail && <span className="text-text-muted"> | </span>}
          {detail}
        </span>
      )}
    </div>
  );
}

/** 연보라 지표 박스 */
export function ListCardMetrics({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-2 rounded-md bg-point-subtle p-2 text-body-sm text-text-secondary">{children}</div>;
}

/** ★ 평점 (건수) */
export function StarRating({ rating, count }: { rating: number; count: number }) {
  return (
    <span className="inline-flex items-center gap-1">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-point" aria-hidden="true">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26"/>
      </svg>
      <b className="font-bold text-point">{rating.toFixed(1)}</b>
      <span className="text-point">({count})</span>
    </span>
  );
}

/** 라벨/값 목록 */
export function InfoList({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-2 text-body-sm">{children}</div>;
}

export function InfoRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-2">
      <span className="w-24 shrink-0 font-bold text-text">{label}</span>
      <span className="min-w-0 flex-1 break-keep text-text-secondary">{children}</span>
    </div>
  );
}

/** 하단 버튼 영역 — 2열, 카드 바닥 정렬. 한 줄 전체 버튼은 col-span-2 */
export function ListCardActions({ children }: { children: ReactNode }) {
  return <div className="mt-auto grid grid-cols-2 gap-2 pt-2">{children}</div>;
}
